import React, { useState, useRef, useContext, useEffect, useMemo, useCallback } from "react"
import { CSVReader } from "react-papaparse"
import { Dialog, DialogTitle, DialogActions, DialogContent, IconButton, CircularProgress, Tabs, Tab, Box, Switch, FormControlLabel } from "@material-ui/core"
import { Publish, ErrorOutline, Edit, SaveAlt } from '@material-ui/icons'
import InventoryEdit from "components/Inventory/InventoryEdit"
import Button from "components/common/Button"
import ErrorDialog from "components/common/ErrorDialog"
import ProgressBar, { formatPercent } from "components/common/ProgressBar"
import { commas, useDebounce, useRestApiPost, useLazyRestApiPost } from "helpers"
import MUIDataTable from "mui-datatables"
import { useInvTableImport } from "components/Inventory/InventoryTableImport"
import { ReducerContext } from "App"
import {
  SAND_RAILCAR_CAPACITY_LBS,
  ETHANOL_RAILCAR_CAPACITY_GAL,
  SAND_PRODUCTS,
  isEthanolProduct,
  isEthanolTerminal
} from "constants/productTypes"

// Product category tabs configuration
const PRODUCT_CATEGORIES = {
  SAND: { label: 'Sand', filter: SAND_PRODUCTS, uom: 'lbs', capacity: SAND_RAILCAR_CAPACITY_LBS },
  CEMENT: { label: 'Cement', filter: ['Cement'], uom: 'lbs', capacity: SAND_RAILCAR_CAPACITY_LBS },
  ETHANOL: { label: 'Ethanol', filter: ['Ethanol'], uom: 'gallons', capacity: ETHANOL_RAILCAR_CAPACITY_GAL }
}

const capacityFor = sandType => (isEthanolProduct(sandType) ? PRODUCT_CATEGORIES.ETHANOL : PRODUCT_CATEGORIES.SAND).capacity

// Map tab keys for product counts lookup
const TAB_KEYS = ['sand', 'cement', 'ethanol']

const InventoryTable = () => {
  const { state: { currentTerminal } } = useContext(ReducerContext)
  const fileInput = useRef(null)
  const [open, setOpen] = useState(false)
  const [data, setData] = useState([])
  const [specificInventoryData, setSpecificInventoryData] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [productCounts, setProductCounts] = useState(null)
  const [searchAllProducts, setSearchAllProducts] = useState(true)

  // Determine which tabs to show based on terminal
  const showAllTabs = isEthanolTerminal(currentTerminal)
  const availableTabs = useMemo(() => showAllTabs
    ? [PRODUCT_CATEGORIES.SAND, PRODUCT_CATEGORIES.CEMENT, PRODUCT_CATEGORIES.ETHANOL]
    : [PRODUCT_CATEGORIES.SAND], [showAllTabs])

  const currentCategory = availableTabs[activeTab] || PRODUCT_CATEGORIES.SAND
  const currentUom = currentCategory.uom

  const initSqlParams = useMemo(() => ({
    search_text: null,
    page_number: 0,
    sort: { column: 'updated_at', direction: 'desc' },
    filter: {},
    num_per_page: 20,
    currentTerminal,
    productFilter: currentCategory.filter,
    includeProductCounts: showAllTabs && searchAllProducts
  }), [currentTerminal, currentCategory.filter, showAllTabs, searchAllProducts])

  const [sqlParams, setSqlParams] = useState(initSqlParams)

  const { data: termInvData, loading: termInvLoading, error: termInvError } = useRestApiPost('api/terminv', initSqlParams)

  const [getTermInv, { data: getTermInvData, loading: getTermInvLoading, error: getTermInvError }] = useLazyRestApiPost('api/terminv')

  // Handle response format based on whether counts are included
  const processResponse = useCallback((response) => {
    if (!response) return

    // Handle new format with productCounts
    if (response.meta?.productCounts) {
      setData(response.data)
      setProductCounts(response.meta.productCounts)
    } else {
      // Legacy format (array)
      setData(response)
      setProductCounts(null)
    }
  }, [])

  useEffect(() => {
    processResponse(termInvData)
  }, [termInvData, processResponse])

  useEffect(() => {
    processResponse(getTermInvData)
  }, [getTermInvData, processResponse])

  const getTermInvDebounced = useDebounce(getTermInv, 1000)
  const { handleReadCSV, csvErrors, importCount, setCsvErrors, setImportCount } = useInvTableImport(getTermInv, initSqlParams)

  const handleClose = () => setOpen(false)

  const handleClick = rowIndex => {
    setSpecificInventoryData(data[rowIndex])
    setOpen(true)
  }

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue)
    // Reset data and fetch for new category, preserving current search/filter
    setData([])
    const newCategory = availableTabs[newValue]
    const newParams = { ...sqlParams, productFilter: newCategory.filter, page_number: 0 }
    setSqlParams(newParams)
    getTermInv(newParams)
  }

  // Dynamic column labels - use product-agnostic labels for multi-product terminals
  const quantityLabel = `Quantity (${currentUom === 'gallons' ? 'gal' : 'lbs'})`
  const productTypeLabel = showAllTabs ? 'Product Type' : 'Sand Type'

  const columns = [
    { label: "Actions", name: "Edit", options: { filter: false, sort: false, empty: true, customBodyRenderLite: (_, rowIndex) => <IconButton onClick={() => handleClick(rowIndex)}><Edit /></IconButton> } },
    { label: "Inventory ID", name: "inventory_id", editable: "never" },
    { label: "Owner", name: "owner" },
    { label: "Filled %", name: "quantity", options: { customBodyRenderLite: (_, rowIndex) => <ProgressBar value={data[rowIndex].quantity} max={capacityFor(data[rowIndex].sand_type)} /> } },
    { label: "Storage Type", name: "storage_type" },
    { label: productTypeLabel, name: "sand_type" },
    { label: "Status", name: "status" },
    { label: quantityLabel, name: "quantity", options: { customBodyRenderLite: (_, rowIndex) => commas(data[rowIndex].quantity) } },
    { label: "Source Location", name: "source_location" },
    { label: "Transloading Client", name: "transloading_client" }
  ]

  const col_names = columns.map(col => col.name)

  const options = {
    selectableRows: 'none',
    searchAlwaysOpen: true,
    print: false,
    viewColumns: false,
    filterType: 'textField',
    serverSide: true,
    page: sqlParams.page_number,
    count: data[0] ? Number(data[0].total_rows) : 0,
    rowsPerPage: sqlParams.num_per_page,
    rowsPerPageOptions: [20, 50, 100],
    customToolbar: () => <>{(getTermInvLoading || termInvLoading) && <CircularProgress />}</>,
    onChangePage: page_number => {
      setSqlParams({ ...sqlParams, page_number })
      getTermInv({ ...sqlParams, page_number })
    },
    onChangeRowsPerPage: num_per_page => {
      setSqlParams({ ...sqlParams, num_per_page })
      getTermInv({ ...sqlParams, num_per_page })
    },
    onColumnSortChange: (column, direction) => {
      setSqlParams({ ...sqlParams, sort: { column, direction }, page_number: 0 })
      getTermInv({ ...sqlParams, sort: { column, direction }, page_number: 0 })
    },
    onFilterChange: (_, filterList) => {
      setSqlParams({ ...sqlParams, filter: Object.fromEntries(filterList.flatMap((item, ind) => item[0] ? [[col_names[ind], item[0]]] : [])), page_number: 0 })
      getTermInvDebounced({ ...sqlParams, filter: Object.fromEntries(filterList.flatMap((item, ind) => item[0] ? [[col_names[ind], item[0]]] : [])), page_number: 0 })
    },
    onSearchChange: search_text => {
      setSqlParams({ ...sqlParams, search_text, page_number: 0 })
      getTermInvDebounced({ ...sqlParams, search_text, page_number: 0 })
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const cols = columns.filter(item => item.label !== 'Actions')
      const dataFormatted = data.map(item => {
        const [, inventory_id, owner, quantity, storage_type, sand_type, ...rest] = item.data
        return { ...item, data: [inventory_id, owner, formatPercent(quantity, capacityFor(sand_type)), storage_type, sand_type, ...rest] }
      })

      return buildHead(cols) + buildBody(dataFormatted)
    }
  }

  const components = { icons: { DownloadIcon: SaveAlt } }

  return (
    <>
      {(termInvError || getTermInvError) && <ErrorDialog error={termInvError || getTermInvError} />}

      {/* Product category tabs - only show if multiple categories available (Binghamton) */}
      {showAllTabs && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="inventory category tabs">
            {availableTabs.map((category, index) => {
              const count = searchAllProducts ? productCounts?.[TAB_KEYS[index]] : undefined
              const label = count !== undefined ? `${category.label} (${count})` : category.label
              return <Tab key={category.label} label={label} id={`inventory-tab-${index}`} />
            })}
          </Tabs>
          <FormControlLabel
            control={
              <Switch
                checked={searchAllProducts}
                onChange={e => {
                  setSearchAllProducts(e.target.checked)
                  setProductCounts(null)
                  const newParams = { ...sqlParams, includeProductCounts: e.target.checked, page_number: 0 }
                  setSqlParams(newParams)
                  getTermInv(newParams)
                }}
                color="primary"
                size="small"
              />
            }
            label="Search all products"
            style={{ marginRight: 8 }}
          />
        </Box>
      )}

      <CSVReader onFileLoad={handleReadCSV} ref={fileInput}>
        {() => <Button onClick={e => fileInput.current && fileInput.current.open(e)} color="primary" style={{ margin: "15px" }}> <Publish />Import</Button>}
      </CSVReader>

      <MUIDataTable title={<CircularProgress />} {...{ data, columns, options, components }} />

      <Dialog open={csvErrors.length > 0}>
        <DialogTitle style={{ fill: '#f44336', color: "#f44336" }} ><ErrorOutline style={{ fontSize: "34px" }} /> An error has occured</DialogTitle>
        <DialogContent>
          {csvErrors.map(e => (
            <div xs={12} key={e.row}>
              <p>Errors on row {e.row}</p>
              <ul>{e.errors.map((err, ind) => <li key={ind}>{err}</li>)}</ul>
            </div>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setCsvErrors([])}>OK</Button></DialogActions>
      </Dialog>

      <Dialog open={importCount > 0}>
        <DialogTitle>{"Success"}</DialogTitle>
        <DialogContent>{importCount} inventory locations were updated or added.</DialogContent>
        <DialogActions><Button onClick={() => setImportCount(0)}>OK</Button></DialogActions>
      </Dialog>
      {open && <InventoryEdit {...{ open, handleClose, specificInventoryData, getTermInv, sqlParams }} />}
    </>
  )
}

export default InventoryTable
